import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import type { ApprovalVerdict, ToolUseItem } from '../../../contracts';
import type { DesktopControlLeaseSnapshot } from '../../../contracts/projects/desktop-control-approval.types';
import { readToolResultText } from '../../../contracts/toolResult';
import type { BlockStatus, LiveDelegateStep } from '../../../data';
import {
  computerUseMessages,
  getPendingApprovalIdForItem,
  isComputerUseCancellationMessage,
  readComputerUseProgressRecap,
  readDesktopRequired,
  readTaskFromInput,
  recordedWorkflowUntitledMessage,
} from '../../../data';
import type { ComputerUseToolUseHost } from '../components/ComputerUseTaskBlock.host';
import { ComputerUseToolUseView } from '../components/ComputerUseToolUseView';

/** Approval-requested fires once when the pending UI is shown, not on resolve. */
const approvalRequestedIds = new Set<string>();

interface RenderComputerUseToolUseDeps {
  agentRunId?: string;
  liveDelegateStep?: LiveDelegateStep;
  desktopControlLease?: DesktopControlLeaseSnapshot | null;
  desktopTargetEnabled?: boolean;
  onResolveApproval?: (
    approvalId: string,
    verdict: ApprovalVerdict,
    reason?: string,
    modifiedInput?: Record<string, unknown> | null
  ) => Promise<void>;
  resolvingApproval?: boolean;
  requireComputerUsePermissions?: () => Promise<boolean>;
  previousUserText?: string;
  intl: IntlShape;
  /** The turn-level failure card owns terminal recovery guidance. */
  turnFailed?: boolean;
}

export const renderComputerUseToolUse = (
  item: ToolUseItem,
  deps: RenderComputerUseToolUseDeps,
  status: BlockStatus,
  awaitingApproval: boolean,
  host: ComputerUseToolUseHost,
  options?: { labelledByRow?: boolean }
): ReactNode => {
  const isActiveLive = !item.isSubAgent && deps.liveDelegateStep?.toolCallId === item.id;
  const isActiveSubAgent =
    item.isSubAgent === true && deps.liveDelegateStep?.toolCallId === item.id;
  const isActive = isActiveLive || isActiveSubAgent;

  const terminalReason = status === 'error' ? readToolResultText(item) : undefined;
  // A desktop task that never started because the desktop was busy.
  const busyReason = host.desktopControl.describeBusyToolResult(terminalReason);
  const displayTerminalReason = busyReason ?? terminalReason;
  const cancellationReason =
    item.approval?.status === 'denied'
      ? deps.intl.formatMessage(computerUseMessages.notStartedReason)
      : status === 'cancelled' || isComputerUseCancellationMessage(terminalReason)
        ? deps.intl.formatMessage(computerUseMessages.stoppedReason)
        : undefined;
  const cardStatus = cancellationReason || busyReason !== null ? 'cancelled' : status;
  const progressRecap =
    cardStatus === 'cancelled'
      ? readComputerUseProgressRecap(item.result_provider_metadata)
      : undefined;
  const pendingApprovalId = getPendingApprovalIdForItem(item);
  const desktopControlledApproval = {
    approvalId: pendingApprovalId ?? item.id,
    agentRunId: deps.agentRunId,
    toolCallId: item.id,
    toolName: item.name,
    approvalKind: item.approval?.approval_kind,
    toolInput: item.input,
  };
  const approveBlockedMessage = host.desktopControl.readBlockedMessage(
    deps.desktopControlLease,
    desktopControlledApproval
  );
  const resolvePendingApproval =
    pendingApprovalId && deps.onResolveApproval && !deps.resolvingApproval
      ? async (verdict: ApprovalVerdict): Promise<void> => {
          if (verdict === 'approve' && deps.requireComputerUsePermissions) {
            const permissionsOk = await deps.requireComputerUsePermissions();
            if (!permissionsOk) return;
          }
          let resolved: boolean;
          if (deps.desktopTargetEnabled) {
            // Native target admission acquires the executor lease. Reserving
            // the legacy frontend lease here blocks that same executor.
            await deps.onResolveApproval!(pendingApprovalId, verdict, '', null);
            resolved = true;
          } else {
            const resolution = await host.desktopControl.resolve(
              {
                approval: desktopControlledApproval,
                verdict,
                owner: {
                  label: readTaskFromInput(item.input),
                },
                resolve: () =>
                  deps.onResolveApproval!(pendingApprovalId, verdict, '', null),
              },
              deps.intl
            );
            resolved = resolution.resolved;
          }
          if (!resolved) return;
          host.onTrack({
            action: 'resolved',
            outcome: verdict === 'approve' ? 'approved' : 'denied',
            object_type: 'cua',
            agent_run_id: deps.agentRunId,
            surface: 'project_chat',
          });
        }
      : undefined;

  if (
    awaitingApproval &&
    pendingApprovalId &&
    !approvalRequestedIds.has(pendingApprovalId)
  ) {
    approvalRequestedIds.add(pendingApprovalId);
    host.onTrack({
      action: 'requested',
      object_type: 'cua',
      agent_run_id: deps.agentRunId,
      surface: 'project_chat',
    });
  }

  const task = readTaskFromInput(item.input);
  const shouldHideTask = taskMatchesUserPromptWithoutSlashCommand(
    task,
    deps.previousUserText
  );
  const desktopRequired = readDesktopRequired(item);

  const isDelegatedSubAgent = item.isSubAgent === true && Boolean(item.childAgentRunId);

  return (
    <ComputerUseToolUseView
      labelledByRow={options?.labelledByRow}
      toolCallId={item.id}
      childAgentRunId={isDelegatedSubAgent ? item.childAgentRunId : undefined}
      status={awaitingApproval ? 'running' : cardStatus}
      awaitingApproval={awaitingApproval}
      task={shouldHideTask ? undefined : task}
      toolCallDisplay={item.toolCallDisplay}
      recordedWorkflow={host.readRecordedWorkflow(
        item.provider_metadata,
        item.inputMerge,
        {
          untitledFallback: deps.intl.formatMessage(
            recordedWorkflowUntitledMessage.untitled
          ),
        }
      )}
      liveStepCount={
        isActive && status === 'running' ? deps.liveDelegateStep?.count : undefined
      }
      trace={isActive ? deps.liveDelegateStep?.trace : undefined}
      childRunApproval={
        isActiveSubAgent && status === 'running'
          ? deps.liveDelegateStep?.childRunApproval
          : undefined
      }
      onCancel={
        status === 'running' && isActive ? deps.liveDelegateStep?.onCancel : undefined
      }
      stopping={
        status === 'running' && isActive
          ? deps.liveDelegateStep?.cancelRequested
          : undefined
      }
      onApprove={
        resolvePendingApproval ? () => resolvePendingApproval('approve') : undefined
      }
      onDeny={resolvePendingApproval ? () => resolvePendingApproval('deny') : undefined}
      approveBlockedMessage={approveBlockedMessage ?? undefined}
      terminalReason={
        desktopRequired || deps.turnFailed
          ? undefined
          : (cancellationReason ?? displayTerminalReason)
      }
      desktopRequired={desktopRequired}
      progressRecap={progressRecap}
      host={host}
    />
  );
};

const taskMatchesUserPromptWithoutSlashCommand = (
  task: string | undefined,
  userText: string | undefined
): boolean => {
  const normalizedTask = normalizePromptForComparison(task);
  if (!normalizedTask) return false;

  const normalizedUserText = normalizePromptForComparison(userText);
  const normalizedUserTextWithoutSkill = normalizePromptForComparison(
    userText?.replace(/(?:^|\s)\/[A-Za-z0-9][A-Za-z0-9_-]*/g, ' ')
  );

  return (
    normalizedTask === normalizedUserTextWithoutSkill &&
    normalizedUserText !== normalizedUserTextWithoutSkill
  );
};

const normalizePromptForComparison = (value: string | undefined): string =>
  value?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';
